import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { FaChevronLeft, FaChevronRight, FaDownload, FaUser, FaCalendarAlt, FaCalendar } from "react-icons/fa";
import '../styles/EmployeeAttendance.css';
import AttendanceStrategyFactory from '../services/attendance/AttendanceStrategyFactory';

function EmployeeAttendance() {
     const token = sessionStorage.getItem("token");
     const BASE_URL = process.env.REACT_APP_BASE_URL;
     const [employees, setEmployees] = useState([]);
     const [selectedEmployee, setSelectedEmployee] = useState("");
     const [attendance, setAttendance] = useState({ days: [] });
     const [selectedMonth, setSelectedMonth] = useState("");
     const [selectedToMonth, setSelectedToMonth] = useState("");
     const [employeeInputValue, setEmployeeInputValue] = useState("");
     const [currentPage, setCurrentPage] = useState(1);
     const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
     const [employeeType, setEmployeeType] = useState(""); // NEW: Track employee type
     const [attendanceStrategy, setAttendanceStrategy] = useState(null); // NEW: Strategy instance
     const selectedEmployeeObj = employees.find(emp => emp.employee_code === selectedEmployee);
     const selectedEmployeeName = selectedEmployeeObj ? selectedEmployeeObj.employee_name : "";

     const days = Array.isArray(attendance.days) ? attendance.days : [];
     // Group days by month
     const groupDaysByMonth = () => {
          const grouped = {};
          days.forEach(day => {
               const monthKey = day.date.substring(0, 7); // Get YYYY-MM
               if (!grouped[monthKey]) {
                    grouped[monthKey] = [];
               }
               grouped[monthKey].push(day);
          });
          return Object.values(grouped);
     };

     const monthGroups = groupDaysByMonth();
     const totalPages = monthGroups.length;
     const paginatedDays = monthGroups[currentPage - 1] || [];

     useEffect(() => {
          const handleResize = () => {
               setIsMobile(window.innerWidth <= 768);
          };
          window.addEventListener('resize', handleResize);
          return () => window.removeEventListener('resize', handleResize);
     }, []);

     const fetchEmployees = async () => {
          try {
               const response = await fetch(`${BASE_URL}/getEmployees`, {
                    method: "GET",
                    headers: {
                         "Content-Type": "application/json",
                         "Authorization": `Bearer ${token}`,
                    },
               });
               const result = await response.json();
               setEmployees(Array.isArray(result.data) ? result.data : []);
          } catch (error) {
               console.error("Error fetching employees:", error);
          }
     };

     const fetchAttendance = async () => {
          try {
               // Validate inputs
               if (!selectedEmployee) {
                    console.error('❌ No employee selected');
                    return;
               }
               if (!selectedMonth || !selectedToMonth) {
                    console.error('❌ No date range selected');
                    return;
               }

               const url = `${BASE_URL}/getEmployeeAttendance`;
               const payload = {
                    employee_code: selectedEmployee,
                    selectedMonth: selectedMonth,
                    selectedToMonth: selectedToMonth
               };

               console.log('📤 Fetching attendance:', payload);

               const response = await fetch(url, {
                    method: 'POST',
                    headers: {
                         'Content-Type': 'application/json',
                         'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify(payload)
               });

               if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
               }

               const json = await response.json();
               console.log('📥 Full API Response:', json);

               if (json.status && json.data) {
                    // Build regularize map from top-level "regularize" array (if any)
                    const regList = Array.isArray(json.regularize) ? json.regularize : [];
                    const regByDate = {};
                    regList.forEach(r => {
                         if (r && r.for_date) {
                              regByDate[r.for_date] = regByDate[r.for_date] || [];
                              regByDate[r.for_date].push(r);
                         }
                    });

                    // Ensure days array exists and attach regularize entries per day
                    const daysArr = Array.isArray(json.data.days) ? json.data.days : [];
                    const enrichedDays = daysArr.map(d => ({ ...d, regularize: regByDate[d.date] || [] }));

                    // Add any regularize-only dates (not present in days) so UI will show them
                    const regOnlyDates = Object.keys(regByDate).filter(d => !daysArr.some(x => x.date === d));
                    const regOnlyDays = regOnlyDates.map(d => ({
                         date: d,
                         punch_in: null,
                         punch_out: null,
                         all_punches: [],
                         shift_total_minutes: 0,
                         worked_seconds: 0,
                         shortfall_seconds: 0,
                         shortfall_hours: '00:00:00',
                         has_shortfall: false,
                         regularize: regByDate[d]
                    }));

                    // Merge and sort by date ascending
                    const mergedDays = [...enrichedDays, ...regOnlyDays].sort((a, b) => a.date.localeCompare(b.date));

                    // Also attach regularize top-level to attendance payload for reference
                    const attendanceData = {
                         ...json.data,
                         days: mergedDays,
                         regularize: regList
                    };

                    console.log('✅ Setting attendance with', attendanceData.days.length, 'days, regularize count', regList.length);
                    setAttendance(attendanceData);

                    // Get employee type and strategy with fallback
                    const empType = json.data.employee_info?.emp_type || 'Contractual_Employee';
                    const splitFlag = json.data.employee_info?.split_shift || 'N'; // NEW: read split shift flag
                    console.log('👤 Employee Type:', empType, 'Split Shift:', splitFlag);
                    setEmployeeType(empType);

                    try {
                         // PASS splitFlag into factory
                         const strategy = AttendanceStrategyFactory.getStrategy(empType, splitFlag);
                         console.log('📋 Strategy loaded:', strategy?.constructor?.name);
                         setAttendanceStrategy(strategy);
                    } catch (strategyError) {
                         console.error('❌ Error loading strategy:', strategyError);
                         // Use default strategy as fallback (explicit 'N' for split shift)
                         const defaultStrategy = AttendanceStrategyFactory.getStrategy('Contractual_Employee', 'N');
                         setAttendanceStrategy(defaultStrategy);
                    }

                    if (attendanceData.days.length === 0) {
                         console.warn('⚠️ API returned 0 attendance days');
                    }
               } else {
                    console.error('❌ API returned:', { status: json.status, hasData: !!json.data });
                    setAttendance({ days: [] });
               }
          } catch (err) {
               console.error('❌ Error fetching attendance:', err);
               setAttendance({ days: [] });

               // Set default strategy on error
               try {
                    const defaultStrategy = AttendanceStrategyFactory.getStrategy('Contractual_Employee');
                    setAttendanceStrategy(defaultStrategy);
               } catch (fallbackError) {
                    console.error('❌ Even fallback strategy failed:', fallbackError);
               }
          }
     };

     useEffect(() => {
          const splitFlag = attendance.employee_info?.split_shift || 'N';
          if (!employeeType) return;
          try {
               const strategy = AttendanceStrategyFactory.getStrategy(employeeType, splitFlag);
               console.log('🔄 Strategy updated:', strategy?.constructor?.name, 'for', employeeType, 'split:', splitFlag);
               setAttendanceStrategy(strategy);
          } catch (e) {
               console.error('❌ Failed to update strategy:', e);
               setAttendanceStrategy(AttendanceStrategyFactory.getStrategy('Contractual_Employee', 'N'));
          }
     }, [employeeType, attendance.employee_info?.split_shift]);

     useEffect(() => {
          fetchEmployees();
     }, []);

     useEffect(() => {
          if (selectedMonth && selectedToMonth && selectedEmployee) {
               fetchAttendance();
               setCurrentPage(1);
          }
     }, [selectedMonth, selectedToMonth, selectedEmployee]);

     // NEW: Use strategy to calculate attendance

     const calculateAttendanceStatus = (day, roster, holidays) => {
          // Check if strategy exists first
          if (!attendanceStrategy) {
               return { status: "-", lateMark: "-", workingHours: "-", statusColor: "#94a3b8", remarks: "" };
          }

          // Prepare employee data from attendance state
          const empData = {
               weekAttendance: attendance.weekAttendance || [],
               allDays: attendance.days || [],
               region: attendance.employee_region || 'Other'
          };

          // Get status from strategy
          const result = attendanceStrategy.calculateAttendanceStatus(day, roster, holidays, empData);

          // Get today's date in YYYY-MM-DD format using local time zone
          const now = new Date();
          const todayString = [
               now.getFullYear(),
               String(now.getMonth() + 1).padStart(2, '0'),
               String(now.getDate()).padStart(2, '0')
          ].join('-');

          // Normalize both dates to ensure clean comparison
          const dayDateNormalized = String(day.date).trim();
          const todayNormalized = todayString.trim();

          const isToday = dayDateNormalized === todayNormalized;
          const hasPunchIn = Boolean(day.punch_in);
          const hasPunchOut = Boolean(day.punch_out);

          // Get current time to check if day is still ongoing
          const currentHour = now.getHours();
          const isDayStillOngoing = currentHour < 23; // Before 11 PM

          // Override LOP to OnDuty for current day if:
          // 1. It's today
          // 2. Employee has punched in
          // 3. Status is LOP
          // 4. Day is still ongoing (optional - before 11 PM)
          if (isToday && hasPunchIn && result.status === "LOP" && isDayStillOngoing) {
               console.log('✅ OVERRIDING TO ONDUTY for date:', dayDateNormalized);
               return {
                    status: "OnDuty",
                    lateMark: result.lateMark || "-",
                    workingHours: hasPunchOut ? result.workingHours : "-",
                    statusColor: "#3b82f6", // Blue
                    remarks: "Currently on duty"
               };
          }

          // If there are approved regularize requests with in/out times, use them to recompute status
          try {
               const approvedRegs = Array.isArray(day.regularize) ? day.regularize.filter(r => (r.status || '').toLowerCase() === 'approved') : [];
               if (approvedRegs.length > 0) {
                    // sum durations from all approved regularize entries that have both times
                    let totalRegSeconds = 0;
                    approvedRegs.forEach(r => {
                         const pin = r.punch_in || r.punchIn || null;
                         const pout = r.punch_out || r.punchOut || null;
                         if (pin && pout) {
                              let inDate = pin.includes('-') || pin.includes('T') ? new Date(pin) : new Date(day.date + ' ' + pin);
                              let outDate = pout.includes('-') || pout.includes('T') ? new Date(pout) : new Date(day.date + ' ' + pout);
                              if (inDate && outDate && outDate > inDate) {
                                   totalRegSeconds += Math.floor((outDate - inDate) / 1000);
                              }
                         }
                    });

                    if (totalRegSeconds > 0) {
                         // determine roster thresholds
                         const shiftHours = parseFloat(roster?.total_hours) || 8;
                         const halfThresholdHours = parseFloat(roster?.WorkingsHoursToBeConsiderdHalfDay) || (shiftHours / 2);

                         const shiftSeconds = Math.floor(shiftHours * 3600);
                         const halfSeconds = Math.floor(halfThresholdHours * 3600);

                         // determine earliest reg in time to compute reg late
                         let regLateMark = '-';
                         try {
                              const regIns = approvedRegs.map(r => r.punch_in || r.punchIn).filter(Boolean);
                              if (regIns.length && roster && roster.in_time) {
                                   const earliestRegIn = regIns.map(pin => (pin.includes('-') || pin.includes('T')) ? new Date(pin) : new Date(day.date + ' ' + pin)).reduce((a, b) => a < b ? a : b);
                                   const rosterIn = roster.in_time ? new Date(day.date + ' ' + roster.in_time) : null;
                                   let allowedIn = rosterIn;
                                   if (roster && roster.grace_in) {
                                        allowedIn = new Date(allowedIn.getTime() + (parseFloat(roster.grace_in || 0) * 60 * 1000));
                                   }
                                   if (allowedIn && earliestRegIn && earliestRegIn > allowedIn) regLateMark = 'Late';
                              }
                         } catch (e) {
                              console.warn('Failed to compute reg late mark', e);
                              regLateMark = '-';
                         }

                         const regWorking = secondsToHMS(totalRegSeconds);

                         // compute new status based on regularize duration
                         if (totalRegSeconds >= shiftSeconds) {
                              // Full present
                              return {
                                   status: 'Present',
                                   lateMark: result.lateMark || '-',
                                   workingHours: regWorking,
                                   statusColor: '#10b981',
                                   remarks: 'Present via regularize',
                                   regWorkingHours: regWorking,
                                   regLateMark: regLateMark
                              };
                         } else if (totalRegSeconds >= halfSeconds) {
                              return {
                                   status: 'Half Day',
                                   lateMark: result.lateMark || '-',
                                   workingHours: regWorking,
                                   statusColor: '#f59e0b',
                                   remarks: 'Half day via regularize',
                                   regWorkingHours: regWorking,
                                   regLateMark: regLateMark
                              };
                         } else {
                              return {
                                   status: 'LOP',
                                   lateMark: result.lateMark || '-',
                                   workingHours: regWorking,
                                   statusColor: '#ef4444',
                                   remarks: 'LOP (regularize insufficient)',
                                   regWorkingHours: regWorking,
                                   regLateMark: regLateMark
                              };
                         }
                    }
               }
          } catch (e) {
               console.error('Error processing regularize override:', e);
          }

          return result;
     };

     const secondsToHMS = (totalSeconds) => {
          if (!totalSeconds) return "-";
          const hours = Math.floor(totalSeconds / 3600);
          const minutes = Math.floor((totalSeconds % 3600) / 60);
          const seconds = totalSeconds % 60;
          return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
     };

     const calculateShortfallSummary = () => {
          let totalShortfallSeconds = 0;
          let totalWorkedSeconds = 0;
          let totalPresentDays = 0;
          let totalWeekendDays = 0;
          let exemptionLimit = "00:00:00"; // Default exemption limit
          let shortfallDaysCount = 0;

          days.forEach(day => {
               const dateObj = new Date(day.date);
               const roster = attendance.duty_roster?.find(r => r.attendance_date === day.date);
               const { status } = calculateAttendanceStatus(day, roster, attendance.holidays);

               // Capture exemption limit once (if present)
               if (roster && roster.exemption_limit && exemptionLimit === "00:00:00") {
                    const exemptionHours = parseFloat(roster.exemption_limit);
                    const hours = Math.floor(exemptionHours);
                    const minutes = Math.round((exemptionHours - hours) * 60);
                    exemptionLimit = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
               }

               // Weekend / weekoff
               const isSunday = dateObj.getDay() === 0;
               const isWeekOff = status === "Week Off" || status === "Weekoff";
               if (isSunday || isWeekOff) totalWeekendDays++;

               // Aggregate shift-level data when present (handles split shifts)
               let dayShortfallSeconds = 0;
               let dayWorked = 0;
               let dayHasShortfall = false;
               let dayPresent = false;

               if (Array.isArray(day.shifts) && day.shifts.length) {
                    day.shifts.forEach(shift => {
                         dayShortfallSeconds += shift.shortfall_seconds || 0;
                         dayWorked += shift.worked_seconds || 0;
                         if (shift.has_shortfall) dayHasShortfall = true;
                         if (shift.punch_in && shift.punch_out) dayPresent = true;
                    });
               } else {
                    dayShortfallSeconds = day.shortfall_seconds || 0;
                    dayWorked = day.worked_seconds || 0;
                    dayHasShortfall = Boolean(day.has_shortfall);
                    dayPresent = Boolean(day.punch_in && day.punch_out);
               }

               totalShortfallSeconds += dayShortfallSeconds;
               totalWorkedSeconds += dayWorked;
               if (dayHasShortfall) shortfallDaysCount++;
               if (dayPresent) totalPresentDays++;
          });

          return {
               shortfallDays: shortfallDaysCount,
               totalShortfall: secondsToHMS(totalShortfallSeconds),
               totalWorkedHours: secondsToHMS(totalWorkedSeconds),
               totalPresentDays: totalPresentDays,
               totalWeekendDays: totalWeekendDays,
               exemptionLimit: exemptionLimit
          };
     };

     // const calculateShortfallSummary = () => {
     //      let totalShortfallSeconds = 0;
     //      let totalWorkedSeconds = 0;
     //      let totalPresentDays = 0;
     //      let totalWeekendDays = 0;
     //      let exemptionLimit = "00:00:00"; // Default exemption limit

     //      days.forEach(day => {
     //           const dateObj = new Date(day.date);
     //           const roster = attendance.duty_roster?.find(r => r.attendance_date === day.date);
     //           const { status } = calculateAttendanceStatus(day, roster, attendance.holidays);

     //           // Get exemption limit from first roster entry (should be same for all)
     //           if (roster && roster.exemption_limit && exemptionLimit === "00:00:00") {
     //                const exemptionHours = parseFloat(roster.exemption_limit);
     //                const hours = Math.floor(exemptionHours);
     //                const minutes = Math.round((exemptionHours - hours) * 60);
     //                exemptionLimit = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
     //           }

     //           // Count weekend days
     //           const isSunday = dateObj.getDay() === 0;
     //           const isWeekOff = status === "Week Off" || status === "Weekoff";
     //           if (isSunday || isWeekOff) {
     //                totalWeekendDays++;
     //           }

     //           // Count shortfall in seconds
     //           if (day.has_shortfall) {
     //                totalShortfallSeconds += day.shortfall_seconds || 0;
     //           }

     //           // Count worked hours and present days
     //           if (day.punch_in && day.punch_out) {
     //                totalPresentDays++;
     //                // Use worked_seconds from backend
     //                totalWorkedSeconds += day.worked_seconds || 0;
     //           }
     //      });

     //      // Convert seconds to HH:MM:SS format
     //      // const secondsToHMS = (totalSeconds) => {
     //      //      const hours = Math.floor(totalSeconds / 3600);
     //      //      const minutes = Math.floor((totalSeconds % 3600) / 60);
     //      //      const seconds = totalSeconds % 60;
     //      //      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
     //      // };



     //      return {
     //           shortfallDays: days.filter(day => day.has_shortfall).length,
     //           totalShortfall: secondsToHMS(totalShortfallSeconds),
     //           totalWorkedHours: secondsToHMS(totalWorkedSeconds),
     //           totalPresentDays: totalPresentDays,
     //           totalWeekendDays: totalWeekendDays,
     //           exemptionLimit: exemptionLimit
     //      };
     // };
     // Add to your JSX before the table
     const shortfallSummary = calculateShortfallSummary();

     const handleDownloadExcel = () => {
          if (!days.length) return;

          const data = days.map(day => {
               const dateObj = new Date(day.date);
               const dd = String(dateObj.getDate()).padStart(2, "0");
               const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
               const yyyy = dateObj.getFullYear();
               const formattedDate = `${dd}-${mm}-${yyyy}`;

               const roster = attendance.duty_roster?.find(r => r.attendance_date === day.date);
               const { status, lateMark, workingHours, remarks, regWorkingHours, regLateMark } = calculateAttendanceStatus(day, roster, attendance.holidays);

               // Format times for display
               const formatTimeForExcel = (timeString) => {
                    if (!timeString) return "-";
                    if (timeString.includes('T') || timeString.includes(' ')) {
                         return new Date(timeString).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                              hour12: false
                         });
                    }
                    return timeString;
               };

               // Convert seconds to HH:MM:SS format
               const secondsToHMS = (seconds) => {
                    const hours = Math.floor(seconds / 3600);
                    const minutes = Math.floor((seconds % 3600) / 60);
                    const secs = seconds % 60;
                    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
               };

               return {
                    // Employee Information
                    "Employee Code": selectedEmployee,
                    "Employee Name": selectedEmployeeName,
                    "Employee Type": employeeType,

                    // Date Information
                    "Date": formattedDate,
                    "Day": ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][dateObj.getDay()],

                    // Punch Information
                    "Punch In": formatTimeForExcel(day.punch_in),
                    "Punch Out": formatTimeForExcel(day.punch_out),

                    // Working Hours & Shortfall
                    "Working Hours": workingHours || "-",
                    "Worked Seconds": day.worked_seconds || 0,
                    "Worked Hours (HH:MM:SS)": day.worked_seconds ? secondsToHMS(day.worked_seconds) : "-",
                    "Shortfall Seconds": day.shortfall_seconds || 0,
                    "Shortfall Hours (HH:MM:SS)": day.shortfall_hours || "00:00:00",
                    "Has Shortfall": day.has_shortfall ? "Yes" : "No",

                    // Shift Information
                    "Shift Name": roster?.shift_name || "-",
                    "Shift In Time": roster?.in_time || "-",
                    "Shift Out Time": roster?.out_time || "-",
                    "Shift Total Hours": roster?.total_hours || "-",
                    "Shift Total Minutes": roster?.total_minutes || "-",

                    // Grace Period Information
                    "Grace In (Minutes)": roster?.grace_in || "-",
                    "Grace Out (Minutes)": roster?.grace_out || "-",
                    "Exemption Limit (Hours)": roster?.exemption_limit || "-",

                    // Attendance Status
                    "Status": status,
                    "Late Mark": lateMark,

                    // Week Off Information
                    "Week Off Type": attendance.employee_info?.week_off || "-",
                    "Is Custom Week Off": roster?.custom_weekoff_date ? "Yes" : "No",
                    "Custom Week Off Date": roster?.custom_weekoff_date || "-",

                    // Additional Information
                    "Remarks": remarks || "",
                    "Regularized Hours": regWorkingHours || "-",
                    "Regularized Late": regLateMark || "-",
                    // Regularize details
                    "Regularized": (Array.isArray(day.regularize) && day.regularize.length > 0) ? 'Yes' : 'No',
                    "Reg Count": Array.isArray(day.regularize) ? day.regularize.length : 0,
                    "Reg Details": (Array.isArray(day.regularize) ? day.regularize.map(r => {
                         const approver = r.approved_by_name || r.approver_name || r.approved_by || r.approver || r.approverName || '';
                         const attCount = Array.isArray(r.attachments) ? r.attachments.length : (Array.isArray(r.attachment_ids) ? r.attachment_ids.length : 0);
                         const inT = (r.punch_in || r.punchIn) ? (r.punch_in || r.punchIn) : '-';
                         const outT = (r.punch_out || r.punchOut) ? (r.punch_out || r.punchOut) : '-';
                         const reason = r.reason || '';
                         return `${inT} / ${outT} (${r.status || ''}${approver ? ' - By: ' + approver : ''}${attCount ? ' - Attachments: ' + attCount : ''}${reason ? ' - ' + reason : ''})`;
                    }).join(' | ') : ''),
                    "Reg Hrs": (Array.isArray(day.regularize) ? (() => {
                         const regSeconds = day.regularize.reduce((acc, r) => {
                              try {
                                   if ((r.status || '').toLowerCase() !== 'approved') return acc;
                                   const inStr = r.punch_in || r.punchIn;
                                   const outStr = r.punch_out || r.punchOut;
                                   if (!inStr || !outStr) return acc;
                                   const [ih, im, is] = inStr.split(':').map(x => Number(x));
                                   const [oh, om, os] = outStr.split(':').map(x => Number(x));
                                   const startSec = ih * 3600 + im * 60 + is;
                                   const endSec = oh * 3600 + om * 60 + os;
                                   if (endSec >= startSec) return acc + (endSec - startSec);
                              } catch (e) {
                                   return acc;
                              }
                              return acc;
                         }, 0);
                         return regSeconds ? secondsToHMS(regSeconds) : '-';
                    })() : '-')
               };
          });

          // Create summary data as first sheet
          const summaryData = [{
               "Summary Type": "Employee Information",
               "Employee Code": selectedEmployee,
               "Employee Name": selectedEmployeeName,
               "Employee Type": employeeType,
               "Week Off Type": attendance.employee_info?.week_off || "-",
               "": "",
               "Month Range": `${attendance.month_range?.start_month_name} ${attendance.month_range?.start_year}`,
               "Total Days": attendance.attendance?.total_days || 0,
               "Present Days": attendance.attendance?.present_days || 0,
               "Absent Days": attendance.attendance?.absent_days || 0,
               "": " ",
               "Total Working Days": days.filter(d => d.punch_in && d.punch_out).length,
               "Days with Shortfall": days.filter(d => d.has_shortfall).length,
               "Total Worked Hours": shortfallSummary.totalWorkedHours,
               "Total Shortfall Hours": shortfallSummary.totalShortfall,
               "Weekend Days": shortfallSummary.totalWeekendDays,
               "Exemption Limit": shortfallSummary.exemptionLimit
          }];

          // Create workbook with multiple sheets
          const wb = XLSX.utils.book_new();

          // Add summary sheet
          const summaryWs = XLSX.utils.json_to_sheet(summaryData);
          XLSX.utils.book_append_sheet(wb, summaryWs, "Summary");

          // Add detailed attendance sheet
          const attendanceWs = XLSX.utils.json_to_sheet(data);
          XLSX.utils.book_append_sheet(wb, attendanceWs, "Detailed Attendance");

          // Generate filename with date range
          const filename = `${selectedEmployeeName}_${employeeType}_Attendance_${attendance.month_range?.start}_to_${attendance.month_range?.end}.xlsx`;

          XLSX.writeFile(wb, filename);
     };

     const formatAllPunches = (allPunches) => {
          if (!allPunches || allPunches.length === 0) return '-';

          return allPunches.map(punch => {
               return new Date(punch).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
               });
          }).join(', ');
     };

     // const renderDesktopTable = () => (
     //      <div className="table-responsive">
     //           <table className="attendance-table">
     //                <thead>
     //                     <tr>
     //                          <th>DATE</th>
     //                          <th>DAY</th>
     //                          <th>IN</th>
     //                          <th>OUT</th>
     //                          <th>SHIFT</th>
     //                          <th>TOTAL HRS</th>
     //                          <th>LATE MARK</th>
     //                          <th>STATUS</th>
     //                     </tr>
     //                </thead>
     //                <tbody>
     //                     {paginatedDays.map(day => {
     //                          const dateObj = new Date(day.date);
     //                          const dd = String(dateObj.getDate()).padStart(2, "0");
     //                          const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
     //                          const yyyy = dateObj.getFullYear();
     //                          const formattedDate = `${dd}-${mm}-${yyyy}`;

     //                          // Find roster for the day
     //                          const roster = attendance.duty_roster?.find(r => r.attendance_date === day.date);

     //                          // Use correct roster fields
     //                          const shiftName = roster?.shift_name || "-";
     //                          const shiftIn = roster?.in_time || "-";
     //                          const shiftOut = roster?.out_time || "-";

     //                          // Calculate attendance status
     //                          const { status, lateMark, workingHours, statusColor } = calculateAttendanceStatus(day, roster, attendance.holidays);

     //                          // Weekoff and Sunday highlight
     //                          const isWeekoff = status === "Week Off" || status === "Weekoff";
     //                          const isSunday = dateObj.getDay() === 0;

     //                          // Show working hours only if both punches exist
     //                          const showWorkingHours = day.punch_in && day.punch_out ? workingHours : "-";

     //                          return (
     //                               <tr
     //                                    key={day.date}
     //                                    className={isWeekoff || isSunday ? "weekoff-row" : ""}
     //                                    style={(isWeekoff || isSunday) ? { backgroundColor: '#eff6ff' } : {}}
     //                               >
     //                                    <td>{formattedDate}</td>
     //                                    <td>{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][dateObj.getDay()]}</td>
     //                                    <td>{day.punch_in ? new Date(day.punch_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : "-"}</td>
     //                                    <td>{day.punch_out ? new Date(day.punch_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : "-"}</td>
     //                                    <td>
     //                                         {shiftName !== "-" ? (
     //                                              <>
     //                                                   {shiftName}
     //                                                   <br />
     //                                                   <span style={{ fontSize: "11px", color: "#64748b" }}>
     //                                                        {shiftIn} - {shiftOut}
     //                                                   </span>
     //                                              </>
     //                                         ) : "-"}
     //                                    </td>
     //                                    <td style={{ fontFamily: 'monospace', fontWeight: '600' }}>{showWorkingHours}</td>
     //                                    <td className={lateMark === "Late" ? "late-mark" : ""}>{lateMark}</td>
     //                                    <td>
     //                                         <span className="status-badge" style={{ backgroundColor: statusColor }}>
     //                                              {status}
     //                                         </span>
     //                                    </td>
     //                               </tr>
     //                          );
     //                     })}
     //                </tbody>
     //           </table>
     //      </div>
     // );

     // const renderMobileCards = () => {
     //      return (
     //           <div className="mobile-cards-container">
     //                {paginatedDays.map((day, index) => {
     //                     const dateObj = new Date(day.date);
     //                     const dd = String(dateObj.getDate()).padStart(2, "0");
     //                     const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
     //                     const yyyy = dateObj.getFullYear();
     //                     const formattedDate = `${dd}-${mm}-${yyyy}`;

     //                     const roster = attendance.duty_roster?.find(r => r.attendance_date === day.date);
     //                     const shiftName = roster?.ShiftName || "-";
     //                     const { status, lateMark, workingHours, statusColor } = calculateAttendanceStatus(day, roster, attendance.holidays);

     //                     const isWeekoff = status === "Week Off";
     //                     const isSunday = dateObj.getDay() === 0;

     //                     return (
     //                          <div
     //                               key={day.date || index}
     //                               className="mobile-card-item"
     //                               style={{
     //                                    background: (isWeekoff || isSunday) ? '#eff6ff' : '#ffffff',
     //                                    border: (isWeekoff || isSunday) ? '2px solid #bfdbfe' : '2px solid #e2e8f0'
     //                               }}
     //                          >
     //                               {/* Header */}
     //                               <div className="mobile-card-header" style={{
     //                                    background: (isWeekoff || isSunday) ? '#dbeafe' : '#f8fafc',
     //                                    borderBottom: (isWeekoff || isSunday) ? '2px solid #bfdbfe' : '2px solid #e2e8f0'
     //                               }}>
     //                                    <div className="mobile-card-date-info">
     //                                         <span className="mobile-card-date" style={{
     //                                              color: (isWeekoff || isSunday) ? '#1e3a8a' : '#1e293b'
     //                                         }}>
     //                                              {formattedDate}
     //                                         </span>
     //                                         <span className="mobile-card-day" style={{
     //                                              color: (isWeekoff || isSunday) ? '#1e40af' : '#64748b',
     //                                              background: (isWeekoff || isSunday) ? '#bfdbfe' : '#f1f5f9'
     //                                         }}>
     //                                              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][dateObj.getDay()]}
     //                                         </span>
     //                                    </div>
     //                                    <span className="mobile-card-status" style={{ background: statusColor }}>
     //                                         {status}
     //                                    </span>
     //                               </div>

     //                               {/* Body */}
     //                               <div className="mobile-card-body" style={{
     //                                    background: (isWeekoff || isSunday) ? '#eff6ff' : '#ffffff'
     //                               }}>
     //                                    <div className="mobile-card-row">
     //                                         <span className="mobile-card-label">IN</span>
     //                                         <span className="mobile-card-value" style={{
     //                                              color: (isWeekoff || isSunday) ? '#1e3a8a' : '#1e293b'
     //                                         }}>
     //                                              {day.punch_in ? new Date(day.punch_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : "-"}
     //                                         </span>
     //                                    </div>
     //                                    <div className="mobile-card-row">
     //                                         <span className="mobile-card-label">OUT</span>
     //                                         <span className="mobile-card-value" style={{
     //                                              color: (isWeekoff || isSunday) ? '#1e3a8a' : '#1e293b'
     //                                         }}>
     //                                              {day.punch_out ? new Date(day.punch_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : "-"}
     //                                         </span>
     //                                    </div>
     //                                    <div className="mobile-card-row">
     //                                         <span className="mobile-card-label">SHIFT</span>
     //                                         <span className="mobile-card-value" style={{
     //                                              color: (isWeekoff || isSunday) ? '#1e3a8a' : '#1e293b'
     //                                         }}>
     //                                              {shiftName}
     //                                         </span>
     //                                    </div>
     //                                    <div className="mobile-card-row">
     //                                         <span className="mobile-card-label">TOTAL HRS</span>
     //                                         <span className="mobile-card-value" style={{
     //                                              color: (isWeekoff || isSunday) ? '#1e3a8a' : '#1e293b',
     //                                              fontFamily: 'monospace',
     //                                              fontWeight: '700'
     //                                         }}>
     //                                              {workingHours}
     //                                         </span>
     //                                    </div>
     //                                    {lateMark && lateMark !== "-" && (
     //                                         <div className="mobile-card-row">
     //                                              <span className="mobile-card-label">LATE MARK</span>
     //                                              <span className="mobile-card-value" style={{
     //                                                   color: '#ef4444',
     //                                                   fontWeight: '700'
     //                                              }}>
     //                                                   {lateMark}
     //                                              </span>
     //                                         </div>
     //                                    )}
     //                               </div>
     //                          </div>
     //                     );
     //                })}
     //           </div>
     //      );
     // };

     const formatDate = (dateString) => {
          const dateObj = new Date(dateString);
          const dd = String(dateObj.getDate()).padStart(2, "0");
          const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
          const yyyy = dateObj.getFullYear();
          return `${dd}-${mm}-${yyyy}`;
     };

     const formatTime = (timeString) => {
          if (!timeString) return '-';

          // If it's already a full datetime string
          if (timeString.includes('T') || timeString.includes(' ')) {
               return new Date(timeString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
          }

          // If it's just a time string like "09:00:00"
          if (timeString.match(/^\d{2}:\d{2}:\d{2}$/)) {
               return timeString; // Return full "09:00:00" including seconds
          }

          return timeString;
     };



     const renderDesktopTable = () => (
          <div className="table-responsive">
               <table className="attendance-table">
                    <thead>
                         <tr>
                              <th>DATE</th>
                              <th>DAY</th>
                              <th>IN</th>
                              <th>OUT</th>
                              <th>SHIFT</th>
                              <th>TOTAL HRS</th>
                              <th>LATE MARK</th>
                              <th>STATUS</th>
                              <th>ALL PUNCHES</th>
                         </tr>
                    </thead>
                    <tbody>
                         {paginatedDays.map(day => {
                              const dateObj = new Date(day.date);
                              const dd = String(dateObj.getDate()).padStart(2, "0");
                              const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
                              const yyyy = dateObj.getFullYear();
                              const formattedDate = `${dd}-${mm}-${yyyy}`;

                              const roster = attendance.duty_roster?.find(r => r.attendance_date === day.date);
                              const shiftName = roster?.shift_name || "-";
                              const shiftIn = roster?.in_time || "-";
                              const shiftOut = roster?.out_time || "-";

                              const { status, lateMark, workingHours, statusColor, regWorkingHours, regLateMark } = calculateAttendanceStatus(day, roster, attendance.holidays);

                              const isWeekoff = status === "Week Off" || status === "Weekoff";
                              const isSunday = dateObj.getDay() === 0;
                              const showWorkingHours = day.punch_in && day.punch_out ? workingHours : "-";

                              const isRegularized = Array.isArray(day.regularize) && day.regularize.length > 0;
                              return (
                                   <tr
                                        key={day.date}
                                        className={isWeekoff || isSunday ? "weekoff-row" : ""}
                                        style={
                                             (isWeekoff || isSunday)
                                                  ? { backgroundColor: '#eff6ff' }
                                                  : (isRegularized ? { backgroundColor: '#ecfdf5' } : {})
                                        }
                                   >
                                        <td>{formattedDate}</td>
                                        <td>{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][dateObj.getDay()]}</td>
                                        <td>
                                             <div>{day.punch_in ? new Date(day.punch_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : "-"}</div>
                                             {Array.isArray(day.regularize) && day.regularize.length > 0 && day.regularize[0].punch_in && (
                                                  <div style={{ fontSize: '0.82rem', color: '#059669', marginTop: 4 }}>Reg: {formatTime(day.regularize[0].punch_in)}</div>
                                             )}
                                        </td>
                                        <td>
                                             <div>{day.punch_out ? new Date(day.punch_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : "-"}</div>
                                             {Array.isArray(day.regularize) && day.regularize.length > 0 && day.regularize[0].punch_out && (
                                                  <div style={{ fontSize: '0.82rem', color: '#059669', marginTop: 4 }}>Reg: {formatTime(day.regularize[0].punch_out)}</div>
                                             )}
                                        </td>

                                        <td>
                                             {shiftName !== "-" ? (
                                                  <>
                                                       {shiftName}
                                                       <br />
                                                       <span style={{ fontSize: "11px", color: "#64748b" }}>
                                                            {shiftIn} - {shiftOut}
                                                       </span>
                                                  </>
                                             ) : "-"}
                                        </td>
                                        <td style={{ fontFamily: 'monospace', fontWeight: '600' }}>
                                             <div>{showWorkingHours}</div>
                                             {regWorkingHours && (
                                                  <div style={{ fontSize: '0.82rem', color: '#059669', marginTop: 4 }}>Reg Hrs: {regWorkingHours}</div>
                                             )}
                                        </td>
                                        <td className={lateMark === "Late" ? "late-mark" : ""}>
                                             <div>{lateMark}</div>
                                             {regLateMark && regLateMark !== '-' && (
                                                  <div style={{ fontSize: '0.82rem', color: '#059669', marginTop: 4 }}>Reg: {regLateMark}</div>
                                             )}
                                        </td>
                                        <td>
                                             {isRegularized && (() => {
                                                  const regTooltip = (Array.isArray(day.regularize) ? day.regularize.map(r => {
                                                       const approver = r.approved_by_name || r.approver_name || r.approved_by || r.approver || r.approverName || '';
                                                       const attCount = Array.isArray(r.attachments) ? r.attachments.length : (Array.isArray(r.attachment_ids) ? r.attachment_ids.length : 0);
                                                       return `${r.for_date || r.forDate || r.date} - ${r.status || r.reason || ''}${approver ? ' - By: ' + approver : ''}${attCount ? ' - Attachments: ' + attCount : ''}`;
                                                  }).join('\n') : '') + (regWorkingHours ? '\nReg Hrs: ' + regWorkingHours : '') + (regLateMark && regLateMark !== '-' ? '\nReg Late: ' + regLateMark : '');
                                                  return (<span className={day.regularize.some(r => (r.status || '').toLowerCase() !== 'approved') ? 'badge bg-warning text-dark me-1' : 'badge bg-success me-1'} title={regTooltip}>Reg</span>);
                                             })()}
                                             
                                             <span className="status-badge" style={{ backgroundColor: statusColor }}>
                                                  {status}
                                             </span>
                                        </td>
                                        <td style={{ fontSize: '0.85rem', color: '#475569' }}>
                                             {formatAllPunches(day.all_punches)}
                                        </td>
                                   </tr>
                              );
                         })}
                    </tbody>
               </table>
          </div>
     );

     // Update renderDoctorDesktopTable function
     const renderDoctorDesktopTable = () => {
          return (
               <div className="table-responsive">
                    <table className="attendance-table">
                         <thead>
                              <tr>
                                   <th>DATE</th>
                                   <th>DAY</th>
                                   <th>IN</th>
                                   <th>OUT</th>

                                   <th>HRS Worked</th>
                                   <th>Shift Hours</th>
                                   <th>Shortfall</th>
                                   <th>SHIFT</th>
                                   <th>STATUS</th>
                                   <th>ALL PUNCHES</th>
                              </tr>
                         </thead>
                         <tbody>
                              {paginatedDays.map((day, index) => {
                                   const dateObj = new Date(day.date);
                                   const roster = attendance.duty_roster?.find(r => r.attendance_date === day.date);
                                   const { status, workingHours, statusColor, regWorkingHours, regLateMark } = calculateAttendanceStatus(day, roster, attendance.holidays);

                                   const isPresent = day.punch_in && day.punch_out;
                                   const isWeekOff = status === "Week Off" || status === "Weekoff";
                                   const hasShortfall = isPresent && day.has_shortfall;

                                   let rowClass = '';
                                   if (isWeekOff) {
                                        rowClass = 'weekoff-row';
                                   } else if (hasShortfall) {
                                        rowClass = 'shortfall-row';
                                   }

                                   const shiftHours = (isPresent && roster?.total_hours) ? roster.total_hours : '-';
                                   const shortfallDisplay = isPresent ? (day.shortfall_hours || (hasShortfall ? '00:00' : 'Complete')) : '-';
                                   const shiftDisplay = roster ? `${roster.shift_name || '-'} | ${formatTime(roster.in_time)} - ${formatTime(roster.out_time)}` : '-';

                                   return (
                                        <tr key={index} className={rowClass}>
                                             <td>{formatDate(day.date)}</td>
                                             <td>{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][dateObj.getDay()]}</td>
                                             <td>
                                                  <div>{formatTime(day.punch_in)}</div>
                                                  {Array.isArray(day.regularize) && day.regularize.length > 0 && day.regularize[0].punch_in && (
                                                       <div style={{ fontSize: '0.82rem', color: '#059669', marginTop: 4 }}>Reg: {formatTime(day.regularize[0].punch_in)}</div>
                                                  )}
                                             </td>
                                             <td>
                                                  <div>{formatTime(day.punch_out)}</div>
                                                  {Array.isArray(day.regularize) && day.regularize.length > 0 && day.regularize[0].punch_out && (
                                                       <div style={{ fontSize: '0.82rem', color: '#059669', marginTop: 4 }}>Reg: {formatTime(day.regularize[0].punch_out)}</div>
                                                  )}
                                             </td>
                                             <td style={{ fontFamily: 'monospace', fontWeight: '600' }}>
                                                  <div>{workingHours}</div>
                                                  {regWorkingHours && (
                                                       <div style={{ fontSize: '0.82rem', color: '#059669', marginTop: 4 }}>Reg Hrs: {regWorkingHours}</div>
                                                  )}
                                             </td>
                                             <td style={{ fontFamily: 'monospace' }}>{shiftHours}</td>
                                             <td style={{
                                                  fontFamily: 'monospace',
                                                  fontWeight: '600',
                                                  color: hasShortfall ? '#dc3545' : (isPresent ? '#28a745' : '#6b7280')
                                             }}>
                                                  {shortfallDisplay}
                                             </td>
                                             <td>{shiftDisplay}</td>
                                             <td>
                                                  <span className="status-badge" style={{ backgroundColor: statusColor }}>
                                                       {status || (day.punch_in ? "Present" : "Absent")}
                                                  </span>
                                             </td>
                                             <td style={{ fontSize: '0.85rem', color: '#475569' }}>
                                                  {formatAllPunches(day.all_punches)}
                                             </td>
                                        </tr>
                                   );
                              })}
                         </tbody>
                    </table>
               </div>
          );
     };

     // Update renderMobileCards to include all punches
     const renderMobileCards = () => {
          return (
               <div className="mobile-cards-container">
                    {paginatedDays.map((day, index) => {
                         const dateObj = new Date(day.date);
                         const dd = String(dateObj.getDate()).padStart(2, "0");
                         const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
                         const yyyy = dateObj.getFullYear();
                         const formattedDate = `${dd}-${mm}-${yyyy}`;

                         const roster = attendance.duty_roster?.find(r => r.attendance_date === day.date);
                         const shiftName = roster?.ShiftName || "-";
                         const { status, lateMark, workingHours, statusColor, regWorkingHours, regLateMark } = calculateAttendanceStatus(day, roster, attendance.holidays);

                         const isWeekoff = status === "Week Off";
                         const isSunday = dateObj.getDay() === 0;
                         const isRegularized = Array.isArray(day.regularize) && day.regularize.length > 0;

                         return (
                              <div
                                   key={day.date || index}
                                   className="mobile-card-item"
                                   style={{
                                        background: (isWeekoff || isSunday) ? '#eff6ff' : '#ffffff',
                                        border: (isWeekoff || isSunday) ? '2px solid #bfdbfe' : '2px solid #e2e8f0'
                                   }}
                              >
                                   <div className="mobile-card-header" style={{
                                        background: (isWeekoff || isSunday) ? '#dbeafe' : '#f8fafc',
                                        borderBottom: (isWeekoff || isSunday) ? '2px solid #bfdbfe' : '2px solid #e2e8f0'
                                   }}>
                                        <div className="mobile-card-date-info">
                                             <span className="mobile-card-date" style={{
                                                  color: (isWeekoff || isSunday) ? '#1e3a8a' : '#1e293b'
                                             }}>
                                                  {formattedDate}
                                             </span>
                                             <span className="mobile-card-day" style={{
                                                  color: (isWeekoff || isSunday) ? '#1e40af' : '#64748b',
                                                  background: (isWeekoff || isSunday) ? '#bfdbfe' : '#f1f5f9'
                                             }}>
                                                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][dateObj.getDay()]}
                                             </span>
                                        </div>
                                        {isRegularized && (() => {
                                             const regTooltip = (Array.isArray(day.regularize) ? day.regularize.map(r => {
                                                  const approver = r.approved_by_name || r.approver_name || r.approved_by || r.approver || r.approverName || '';
                                                  const attCount = Array.isArray(r.attachments) ? r.attachments.length : (Array.isArray(r.attachment_ids) ? r.attachment_ids.length : 0);
                                                  return `${r.for_date || r.forDate || r.date} - ${r.status || r.reason || ''}${approver ? ' - By: ' + approver : ''}${attCount ? ' - Attachments: ' + attCount : ''}`;
                                             }).join('\n') : '') + (regWorkingHours ? '\nReg Hrs: ' + regWorkingHours : '') + (regLateMark && regLateMark !== '-' ? '\nReg Late: ' + regLateMark : '');
                                             return (<span className={day.regularize.some(r => (r.status || '').toLowerCase() !== 'approved') ? 'badge bg-warning text-dark me-2' : 'badge bg-success me-2'} title={regTooltip}>Reg</span>);
                                        })()}
                                        <span className="mobile-card-status" style={{ background: statusColor }}>
                                             {status}
                                        </span>
                                   </div>

                                   <div className="mobile-card-body" style={{
                                        background: (isWeekoff || isSunday) ? '#eff6ff' : '#ffffff'
                                   }}>
                                        <div className="mobile-card-row">
                                             <span className="mobile-card-label">IN</span>
                                             <span className="mobile-card-value" style={{
                                                  color: (isWeekoff || isSunday) ? '#1e3a8a' : '#1e293b'
                                             }}>
                                                  {day.punch_in ? new Date(day.punch_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : "-"}
                                                  {Array.isArray(day.regularize) && day.regularize.length > 0 && day.regularize[0].punch_in && (
                                                       <div style={{ fontSize: '0.82rem', color: '#059669', marginTop: 4 }}>Reg: {formatTime(day.regularize[0].punch_in)}</div>
                                                  )}
                                             </span>
                                        </div>
                                        <div className="mobile-card-row">
                                             <span className="mobile-card-label">OUT</span>
                                             <span className="mobile-card-value" style={{
                                                  color: (isWeekoff || isSunday) ? '#1e3a8a' : '#1e293b'
                                             }}>
                                                  {day.punch_out ? new Date(day.punch_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : "-"}
                                                  {Array.isArray(day.regularize) && day.regularize.length > 0 && day.regularize[0].punch_out && (
                                                       <div style={{ fontSize: '0.82rem', color: '#059669', marginTop: 4 }}>Reg: {formatTime(day.regularize[0].punch_out)}</div>
                                                  )}
                                             </span>
                                        </div>
                                        <div className="mobile-card-row">
                                             <span className="mobile-card-label">ALL PUNCHES</span>
                                             <span className="mobile-card-value" style={{
                                                  color: (isWeekoff || isSunday) ? '#1e3a8a' : '#475569',
                                                  fontSize: '0.85rem'
                                             }}>
                                                  {formatAllPunches(day.all_punches)}
                                             </span>
                                        </div>
                                        <div className="mobile-card-row">
                                             <span className="mobile-card-label">SHIFT</span>
                                             <span className="mobile-card-value" style={{
                                                  color: (isWeekoff || isSunday) ? '#1e3a8a' : '#1e293b'
                                             }}>
                                                  {shiftName}
                                             </span>
                                        </div>
                                        <div className="mobile-card-row">
                                             <span className="mobile-card-label">TOTAL HRS</span>
                                             <span className="mobile-card-value" style={{
                                                  color: (isWeekoff || isSunday) ? '#1e3a8a' : '#1e293b',
                                                  fontFamily: 'monospace',
                                                  fontWeight: '700'
                                             }}>
                                                  {workingHours}
                                             </span>
                                        </div>
                                        {lateMark && lateMark !== "-" && (
                                             <div className="mobile-card-row">
                                                  <span className="mobile-card-label">LATE MARK</span>
                                                  <span className="mobile-card-value" style={{
                                                       color: '#ef4444',
                                                       fontWeight: '700'
                                                  }}>
                                                       {lateMark}
                                                       {regLateMark && regLateMark !== '-' && (
                                                            <div style={{ fontSize: '0.82rem', color: '#059669', marginTop: 4 }}>Reg: {regLateMark}</div>
                                                       )}
                                                  </span>
                                             </div>
                                        )}
                                   </div>
                              </div>
                         );
                    })}
               </div>
          );
     };



     // const renderDoctorDesktopTable = () => {
     //      return (
     //           <div className="table-responsive">
     //                <table className="attendance-table">
     //                     <thead>
     //                          <tr>
     //                               <th>DATE</th>
     //                               <th>DAY</th>
     //                               <th>IN</th>
     //                               <th>OUT</th>
     //                               <th>HRS Worked</th>
     //                               <th>Shift Hours</th>
     //                               <th>Shortfall</th>
     //                               <th>SHIFT</th>
     //                               <th>STATUS</th>
     //                          </tr>
     //                     </thead>
     //                     <tbody>
     //                          {paginatedDays.map((day, index) => {
     //                               const dateObj = new Date(day.date);
     //                               const roster = attendance.duty_roster?.find(r => r.attendance_date === day.date);
     //                               const { status, workingHours, statusColor } = calculateAttendanceStatus(day, roster, attendance.holidays);

     //                               // Calculate row styling
     //                               const isPresent = day.punch_in && day.punch_out;
     //                               const isWeekOff = status === "Week Off" || status === "Weekoff";
     //                               const hasShortfall = isPresent && day.has_shortfall;

     //                               // Determine row class
     //                               let rowClass = '';
     //                               if (isWeekOff) {
     //                                    rowClass = 'weekoff-row';
     //                               } else if (hasShortfall) {
     //                                    rowClass = 'shortfall-row';
     //                               }

     //                               // Calculate display values
     //                               const shiftHours = (isPresent && roster?.total_hours) ? roster.total_hours : '-';
     //                               const shortfallDisplay = isPresent ? (day.shortfall_hours || (hasShortfall ? '00:00' : 'Complete')) : '-';
     //                               const shiftDisplay = roster ? `${roster.shift_name || '-'} | ${formatTime(roster.in_time)} - ${formatTime(roster.out_time)}` : '-';

     //                               return (
     //                                    <tr key={index} className={rowClass}>
     //                                         <td>{formatDate(day.date)}</td>
     //                                         <td>{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][dateObj.getDay()]}</td>
     //                                         <td>{formatTime(day.punch_in)}</td>
     //                                         <td>{formatTime(day.punch_out)}</td>
     //                                         <td style={{ fontFamily: 'monospace', fontWeight: '600' }}>{workingHours}</td>
     //                                         <td style={{ fontFamily: 'monospace' }}>{shiftHours}</td>
     //                                         <td style={{
     //                                              fontFamily: 'monospace',
     //                                              fontWeight: '600',
     //                                              color: hasShortfall ? '#dc3545' : (isPresent ? '#28a745' : '#6b7280')
     //                                         }}>
     //                                              {shortfallDisplay}
     //                                         </td>
     //                                         <td>{shiftDisplay}</td>
     //                                         <td>
     //                                              <span className="status-badge" style={{ backgroundColor: statusColor }}>
     //                                                   {status || (day.punch_in ? "Present" : "Absent")}
     //                                              </span>
     //                                         </td>
     //                                    </tr>
     //                               );
     //                          })}
     //                     </tbody>
     //                </table>
     //           </div>
     //      );
     // };

     // Add this function after renderMobileCards
     const renderDoctorMobileCards = () => {
          return (
               <div className="mobile-cards-container">
                    {paginatedDays.map((day, index) => {
                         const dateObj = new Date(day.date);
                         const dd = String(dateObj.getDate()).padStart(2, "0");
                         const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
                         const yyyy = dateObj.getFullYear();
                         const formattedDate = `${dd}-${mm}-${yyyy}`;

                         const roster = attendance.duty_roster?.find(r => r.attendance_date === day.date);
                         const { status, workingHours, statusColor, regWorkingHours, regLateMark } = calculateAttendanceStatus(day, roster, attendance.holidays);

                         const isPresent = day.punch_in && day.punch_out;
                         const isWeekOff = status === "Week Off" || status === "Weekoff";
                         const hasShortfall = isPresent && day.has_shortfall;
                         const isRegularized = Array.isArray(day.regularize) && day.regularize.length > 0;

                         const shiftHours = (isPresent && roster?.total_hours) ? roster.total_hours : '-';
                         const shortfallDisplay = isPresent ? (day.shortfall_hours || (hasShortfall ? '00:00' : 'Complete')) : '-';
                         const shiftDisplay = roster ? `${roster.shift_name || '-'}` : '-';

                         return (
                              <div
                                   key={day.date || index}
                                   className="mobile-card-item"
                                   style={{
                                        background: isRegularized ? '#ecfdf5' : (isWeekOff ? '#eff6ff' : (hasShortfall ? '#fff5f5' : '#ffffff')),
                                        border: isRegularized ? '2px solid #bbf7d0' : (isWeekOff ? '2px solid #bfdbfe' : (hasShortfall ? '2px solid #fecaca' : '2px solid #e2e8f0'))
                                   }}
                              >
                                   <div className="mobile-card-header" style={{
                                        background: isWeekOff ? '#dbeafe' : (hasShortfall ? '#fee2e2' : '#f8fafc'),
                                        borderBottom: isWeekOff ? '2px solid #bfdbfe' : (hasShortfall ? '2px solid #fecaca' : '2px solid #e2e8f0')
                                   }}>
                                        <div className="mobile-card-date-info">
                                             <span className="mobile-card-date" style={{
                                                  color: isWeekOff ? '#1e3a8a' : '#1e293b'
                                             }}>
                                                  {formattedDate}
                                             </span>
                                             <span className="mobile-card-day" style={{
                                                  color: isWeekOff ? '#1e40af' : '#64748b',
                                                  background: isWeekOff ? '#bfdbfe' : '#f1f5f9'
                                             }}>
                                                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][dateObj.getDay()]}
                                             </span>
                                        </div>
                                        {isRegularized && (() => {
                                             const regTooltip = (Array.isArray(day.regularize) ? day.regularize.map(r => {
                                                  const approver = r.approved_by_name || r.approver_name || r.approved_by || r.approver || r.approverName || '';
                                                  const attCount = Array.isArray(r.attachments) ? r.attachments.length : (Array.isArray(r.attachment_ids) ? r.attachment_ids.length : 0);
                                                  return `${r.for_date || r.forDate || r.date} - ${r.status || r.reason || ''}${approver ? ' - By: ' + approver : ''}${attCount ? ' - Attachments: ' + attCount : ''}`;
                                             }).join('\n') : '') + (regWorkingHours ? '\nReg Hrs: ' + regWorkingHours : '') + (regLateMark && regLateMark !== '-' ? '\nReg Late: ' + regLateMark : '');
                                             return (<span className={day.regularize.some(r => (r.status || '').toLowerCase() !== 'approved') ? 'badge bg-warning text-dark me-2' : 'badge bg-success me-2'} title={regTooltip}>Reg</span>);
                                        })()}
                                        <span className="mobile-card-status" style={{ background: statusColor }}>
                                             {status || (day.punch_in ? "Present" : "Absent")}
                                        </span>
                                   </div>

                                   <div className="mobile-card-body" style={{
                                        background: isWeekOff ? '#eff6ff' : (hasShortfall ? '#fff5f5' : '#ffffff')
                                   }}>
                                        <div className="mobile-card-row">
                                             <span className="mobile-card-label">IN</span>
                                             <span className="mobile-card-value" style={{
                                                  color: isWeekOff ? '#1e3a8a' : '#1e293b'
                                             }}>
                                                  {formatTime(day.punch_in)}
                                                  {Array.isArray(day.regularize) && day.regularize.length > 0 && day.regularize[0].punch_in && (
                                                       <div style={{ fontSize: '0.82rem', color: '#059669', marginTop: 4 }}>Reg: {formatTime(day.regularize[0].punch_in)}</div>
                                                  )}
                                             </span>
                                        </div>
                                        <div className="mobile-card-row">
                                             <span className="mobile-card-label">OUT</span>
                                             <span className="mobile-card-value" style={{
                                                  color: isWeekOff ? '#1e3a8a' : '#1e293b'
                                             }}>
                                                  {formatTime(day.punch_out)}
                                                  {Array.isArray(day.regularize) && day.regularize.length > 0 && day.regularize[0].punch_out && (
                                                       <div style={{ fontSize: '0.82rem', color: '#059669', marginTop: 4 }}>Reg: {formatTime(day.regularize[0].punch_out)}</div>
                                                  )}
                                             </span>
                                        </div>
                                        <div className="mobile-card-row">
                                             <span className="mobile-card-label">ALL PUNCHES</span>
                                             <span className="mobile-card-value" style={{
                                                  color: isWeekOff ? '#1e3a8a' : '#475569',
                                                  fontSize: '0.85rem'
                                             }}>
                                                  {formatAllPunches(day.all_punches)}
                                             </span>
                                        </div>
                                        <div className="mobile-card-row">
                                             <span className="mobile-card-label">HRS WORKED</span>
                                             <span className="mobile-card-value" style={{
                                                  color: isWeekOff ? '#1e3a8a' : '#1e293b',
                                                  fontFamily: 'monospace',
                                                  fontWeight: '700'
                                             }}>
                                                  {workingHours}
                                                  {regWorkingHours && (
                                                       <div style={{ fontSize: '0.82rem', color: '#059669', marginTop: 4 }}>Reg Hrs: {regWorkingHours}</div>
                                                  )}
                                             </span>
                                        </div>
                                        <div className="mobile-card-row">
                                             <span className="mobile-card-label">SHIFT HOURS</span>
                                             <span className="mobile-card-value" style={{
                                                  color: isWeekOff ? '#1e3a8a' : '#1e293b',
                                                  fontFamily: 'monospace'
                                             }}>
                                                  {shiftHours}
                                             </span>
                                        </div>
                                        <div className="mobile-card-row">
                                             <span className="mobile-card-label">SHORTFALL</span>
                                             <span className="mobile-card-value" style={{
                                                  fontFamily: 'monospace',
                                                  fontWeight: '700',
                                                  color: hasShortfall ? '#dc3545' : (isPresent ? '#28a745' : '#6b7280')
                                             }}>
                                                  {shortfallDisplay}
                                             </span>
                                        </div>
                                        <div className="mobile-card-row">
                                             <span className="mobile-card-label">SHIFT</span>
                                             <span className="mobile-card-value" style={{
                                                  color: isWeekOff ? '#1e3a8a' : '#1e293b'
                                             }}>
                                                  {shiftDisplay}
                                             </span>
                                        </div>
                                   </div>
                              </div>
                         );
                    })}
               </div>
          );
     };


     // New: Desktop table for doctors with split_shift = "Y"
     const renderDoctorSplitDesktopTable = () => (
          <div className="table-responsive">
               <table className="attendance-table">
                    <thead>
                         <tr>
                              <th>DATE</th>
                              <th>DAY</th>
                              <th>SHIFT 1</th>
                              <th>S1 IN/OUT</th>
                              <th>SHIFT 2</th>
                              <th>S2 IN/OUT</th>
                              <th>TOTAL SHORTFALL</th>
                              <th>STATUS</th>
                              <th>ALL PUNCHES</th>
                         </tr>
                    </thead>
                    <tbody>
                         {paginatedDays.map((day) => {
                              const dateObj = new Date(day.date);
                              const dd = String(dateObj.getDate()).padStart(2, "0");
                              const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
                              const yyyy = dateObj.getFullYear();
                              const formattedDate = `${dd}-${mm}-${yyyy}`;

                              const shift1 = (day.shifts || []).find(s => s.split_shift_type === 'shift_1') || {};
                              const shift2 = (day.shifts || []).find(s => s.split_shift_type === 'shift_2') || {};
                              const s1In = shift1.punch_in ? formatTime(shift1.punch_in) : "-";
                              const s1Out = shift1.punch_out ? formatTime(shift1.punch_out) : "-";
                              const s2In = shift2.punch_in ? formatTime(shift2.punch_in) : "-";
                              const s2Out = shift2.punch_out ? formatTime(shift2.punch_out) : "-";

                              const inStyle = (exists) => ({ color: exists ? '#10b981' : '#6b7280', fontWeight: exists ? 700 : 500 });

                              const totalShortfallSeconds = (shift1.shortfall_seconds || 0) + (shift2.shortfall_seconds || 0);
                              const totalShortfall = totalShortfallSeconds ? secondsToHMS(totalShortfallSeconds) : "-";

                              const rosterForDay = attendance.duty_roster?.filter(r => r.attendance_date === day.date) || [];
                              const { status, statusColor, regWorkingHours, regLateMark } = calculateAttendanceStatus(day, rosterForDay[0], attendance.holidays);

                              return (
                                   <tr key={day.date}>
                                        <td>{formattedDate}</td>
                                        <td>{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][dateObj.getDay()]}</td>
                                        <td>{shift1.shift_name || "-"}</td>
                                        <td><span style={inStyle(!!shift1.punch_in)}>{s1In}</span> / <span style={inStyle(!!shift1.punch_out)}>{s1Out}</span></td>
                                        <td>{shift2.shift_name || "-"}</td>
                                        <td><span style={inStyle(!!shift2.punch_in)}>{s2In}</span> / <span style={inStyle(!!shift2.punch_out)}>{s2Out}</span></td>
                                        <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                             <div>{totalShortfall}</div>
                                             {regWorkingHours && (
                                                  <div style={{ fontSize: '0.82rem', color: '#059669', marginTop: 4 }}>Reg Hrs: {regWorkingHours}</div>
                                             )}
                                             {regLateMark && regLateMark !== '-' && (
                                                  <div style={{ fontSize: '0.82rem', color: '#059669', marginTop: 4 }}>Reg: {regLateMark}</div>
                                             )}
                                        </td>
                                        <td><span className="status-badge" style={{ backgroundColor: statusColor }}>{status}</span></td>
                                        <td style={{ fontSize: '0.85rem', color: '#475569' }}>{formatAllPunches([...(shift1.all_punches || []), ...(shift2.all_punches || [])])}</td>
                                   </tr>
                              );
                         })}
                    </tbody>
               </table>
          </div>
     );

     // New: Mobile cards for doctors with split_shift = "Y"
     const renderDoctorSplitMobileCards = () => (
          <div className="mobile-cards-container">
               {paginatedDays.map((day, index) => {
                    const dateObj = new Date(day.date);
                    const dd = String(dateObj.getDate()).padStart(2, "0");
                    const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
                    const yyyy = dateObj.getFullYear();
                    const formattedDate = `${dd}-${mm}-${yyyy}`;

                    const shift1 = (day.shifts || []).find(s => s.split_shift_type === "shift_1") || {};
                    const shift2 = (day.shifts || []).find(s => s.split_shift_type === "shift_2") || {};

                    const totalShortfallSeconds = (shift1.shortfall_seconds || 0) + (shift2.shortfall_seconds || 0);
                    const totalShortfall = totalShortfallSeconds ? secondsToHMS(totalShortfallSeconds) : "-";

                    const rosterForDay = attendance.duty_roster?.filter(r => r.attendance_date === day.date) || [];
                    const { status, statusColor } = calculateAttendanceStatus(day, rosterForDay[0], attendance.holidays);
                    const isWeekOff = status === "Week Off" || status === "Weekoff";

                    return (
                         <div key={day.date || index} className="mobile-card-item" style={{
                              background: isWeekOff ? '#eff6ff' : '#ffffff',
                              border: isWeekOff ? '2px solid #bfdbfe' : '2px solid #e2e8f0'
                         }}>
                              <div className="mobile-card-header" style={{
                                   background: isWeekOff ? '#dbeafe' : '#f8fafc',
                                   borderBottom: isWeekOff ? '2px solid #bfdbfe' : '2px solid #e2e8f0'
                              }}>
                                   <div className="mobile-card-date-info">
                                        <span className="mobile-card-date">{formattedDate}</span>
                                        <span className="mobile-card-day">{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][dateObj.getDay()]}</span>
                                   </div>
                                   <span className="mobile-card-status" style={{ backgroundColor: statusColor }}>{status}</span>
                              </div>

                              <div className="mobile-card-body" style={{ background: isWeekOff ? '#eff6ff' : '#ffffff' }}>
                                   <div className="mobile-card-row">
                                        <span className="mobile-card-label">Shift 1</span>
                                        <span className="mobile-card-value">{shift1.shift_name || "-"}</span>
                                   </div>
                                   <div className="mobile-card-row">
                                        <span className="mobile-card-label">S1 IN/OUT</span>
                                        <span className="mobile-card-value">
                                             <span style={{ color: shift1.punch_in ? '#10b981' : '#6b7280', fontWeight: shift1.punch_in ? 700 : 500 }}>{shift1.punch_in ? formatTime(shift1.punch_in) : "-"}</span>
                                             {" / "}
                                             <span style={{ color: shift1.punch_out ? '#10b981' : '#6b7280', fontWeight: shift1.punch_out ? 700 : 500 }}>{shift1.punch_out ? formatTime(shift1.punch_out) : "-"}</span>
                                        </span>
                                   </div>

                                   <div style={{ height: 8 }} />

                                   <div className="mobile-card-row">
                                        <span className="mobile-card-label">Shift 2</span>
                                        <span className="mobile-card-value">{shift2.shift_name || "-"}</span>
                                   </div>
                                   <div className="mobile-card-row">
                                        <span className="mobile-card-label">S2 IN/OUT</span>
                                        <span className="mobile-card-value">
                                             <span style={{ color: shift2.punch_in ? '#10b981' : '#6b7280', fontWeight: shift2.punch_in ? 700 : 500 }}>{shift2.punch_in ? formatTime(shift2.punch_in) : "-"}</span>
                                             {" / "}
                                             <span style={{ color: shift2.punch_out ? '#10b981' : '#6b7280', fontWeight: shift2.punch_out ? 700 : 500 }}>{shift2.punch_out ? formatTime(shift2.punch_out) : "-"}</span>
                                        </span>
                                   </div>

                                   <div style={{ height: 8 }} />

                                   <div className="mobile-card-row">
                                        <span className="mobile-card-label">Shortfall</span>
                                        <span className="mobile-card-value" style={{ fontFamily: 'monospace', fontWeight: 600 }}>{totalShortfall}</span>
                                   </div>

                                   <div className="mobile-card-row">
                                        <span className="mobile-card-label">All Punches</span>
                                        <span className="mobile-card-value">{formatAllPunches([...(shift1.all_punches || []), ...(shift2.all_punches || [])])}</span>
                                   </div>
                              </div>
                         </div>
                    );
               })}
          </div>
     );

     // const renderAttendanceView = () => {
     //      if (employeeType === "DOCTOR") {
     //           return isMobile ? renderDoctorMobileCards() : renderDoctorDesktopTable();
     //      } else {
     //           // Default rendering for Contractual_Employee and others
     //           return isMobile ? renderMobileCards() : renderDesktopTable();
     //      }
     // };

     const renderAttendanceView = () => {
          const isDoctorSplit = (employeeType === "DOCTOR") && attendance.employee_info?.split_shift === "Y";
          if (isDoctorSplit) {
               return isMobile ? renderDoctorSplitMobileCards() : renderDoctorSplitDesktopTable();
          } else if (employeeType === "DOCTOR") {
               return isMobile ? renderDoctorMobileCards() : renderDoctorDesktopTable();
          } else {
               return isMobile ? renderMobileCards() : renderDesktopTable();
          }
     };

     return (
          <div className="employee-attendance-wrapper">
               <div className="attendance-header">
                    <h2>Employee Attendance</h2>
                    {employeeType && (
                         <small style={{ marginTop: "5px" }}>
                              -  {employeeType}
                         </small>
                    )}
               </div>

               <div className="attendance-main-card">
                    <div className="filters-container row">
                         <div className="col-12 col-md-3 filter-group employee-filter">
                              <label>
                                   <FaUser /> Employee
                              </label>
                              <input
                                   className="filter-input"
                                   type="text"
                                   placeholder="Select Employee"
                                   list="employees"
                                   value={employeeInputValue}
                                   onChange={e => {
                                        setEmployeeInputValue(e.target.value);
                                        const found = employees.find(emp =>
                                             `${emp.employee_code} - ${emp.employee_name}` === e.target.value
                                        );
                                        setSelectedEmployee(found ? found.employee_code : "");
                                        setAttendance({ days: [] });
                                        setSelectedMonth("");
                                        setSelectedToMonth("");
                                   }}
                                   autoComplete="off"
                              />
                              <datalist id="employees">
                                   {employees.map(emp => (
                                        <option
                                             key={emp.emp_id}
                                             value={`${emp.employee_code} - ${emp.employee_name}`}
                                        />
                                   ))}
                              </datalist>
                         </div>

                         <div className="col-12 col-md-2 filter-group month-filter">
                              <label>
                                   <FaCalendarAlt /> From
                              </label>
                              <input
                                   type="month"
                                   className="filter-input"
                                   min="2025-08"
                                   value={selectedMonth}
                                   onChange={e => {
                                        const newMonth = e.target.value;
                                        setSelectedMonth(newMonth);
                                        setSelectedToMonth(newMonth); // ✅ Automatically set To month same as From month
                                   }}
                              />
                         </div>

                         <div className="col-12 col-md-2 filter-group month-filter">
                              <label>
                                   <FaCalendarAlt /> To
                              </label>
                              <input
                                   type="month"
                                   className="filter-input"
                                   min={selectedMonth}
                                   value={selectedToMonth}
                                   onChange={e => setSelectedToMonth(e.target.value)}
                                   disabled={!selectedMonth}
                              />
                         </div>

                         <div className="col-12 col-md-2 filter-group action-buttons">
                              <button
                                   className="download-btn"
                                   onClick={handleDownloadExcel}
                                   disabled={!days.length}
                              >
                                   <FaDownload /> {!isMobile && "Download Excel"}
                              </button>
                         </div>

                         <div className="col-12 col-md-2 filter-group action-buttons">
                              <Link
                                   to="/employee-attendance/calendar"
                                   state={{
                                        selectedEmployee: selectedEmployee,
                                        selectedEmployeeName: selectedEmployeeName,
                                        selectedMonth: selectedMonth,
                                        employeeType: employeeType
                                   }}
                              >
                                   <button
                                        className="calendar-view-btn"
                                        disabled={!selectedEmployee || !selectedMonth}
                                   >
                                        <FaCalendar /> {!isMobile && "Calendar View"}
                                   </button>
                              </Link>
                         </div>
                    </div>

                    {days.length > 0 ? (
                         <>
                              {/* Summary Cards - Move above table and only show for Doctor */}
                              {employeeType === "DOCTOR" && (
                                   <div className="summary-cards" style={{ marginTop: '20px' }}>
                                        <div className="summary-card">
                                             <h4>Total Present Days</h4>
                                             <span className="summary-number">{shortfallSummary.totalPresentDays}</span>
                                        </div>
                                        <div className="summary-card">
                                             <h4>Total Worked Hours</h4>
                                             <span className="summary-number">{shortfallSummary.totalWorkedHours}</span>
                                        </div>
                                        <div className="summary-card">
                                             <h4>Total Weekend Days</h4>
                                             <span className="summary-number">{shortfallSummary.totalWeekendDays}</span>
                                        </div>
                                        <div className="summary-card">
                                             <h4>Days with Shortfall</h4>
                                             <span className="summary-number">{shortfallSummary.shortfallDays}</span>
                                        </div>
                                        <div className="summary-card">
                                             <h4>Total Shortfall Hours</h4>
                                             <span className="summary-number">{shortfallSummary.totalShortfall}</span>
                                        </div>
                                        <div className="summary-card">
                                             <h4>Exemption Limit</h4>
                                             <span className="summary-number">{shortfallSummary.exemptionLimit}</span>
                                        </div>
                                   </div>
                              )}

                              {employeeType === "CONTRACTUAL_EMPLOYEE" && (
                                   <div className="summary-cards" style={{ marginTop: '20px' }}>
                                        <div className="summary-card">
                                             <h4>Total Present Days</h4>
                                             <span className="summary-number">{shortfallSummary.totalPresentDays}</span>
                                        </div>
                                        <div className="summary-card">
                                             <h4>Total Worked Hours</h4>
                                             <span className="summary-number">{shortfallSummary.totalWorkedHours}</span>
                                        </div>
                                        <div className="summary-card">
                                             <h4>Total Weekend Days</h4>
                                             <span className="summary-number">{shortfallSummary.totalWeekendDays}</span>
                                        </div>

                                   </div>
                              )}

                              {renderAttendanceView()}
                              <div className="pagination-container">
                                   <button
                                        className="pagination-btn"
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                   >
                                        <FaChevronLeft />
                                   </button>
                                   <span className="pagination-info">
                                        Page {currentPage} of {totalPages || 1}
                                   </span>
                                   <button
                                        className="pagination-btn"
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages || totalPages === 0}
                                   >
                                        <FaChevronRight />
                                   </button>
                                   <span className="total-records">
                                        Total: {days.length}
                                   </span>
                              </div>
                         </>
                    ) : (
                         selectedEmployee && selectedMonth && selectedToMonth ? (
                              <div className="no-data-message">
                                   <p>No attendance records found</p>
                              </div>
                         ) : (
                              <div className="no-data-message">
                                   <p>Please select employee and date range to view attendance</p>
                              </div>
                         )
                    )}
               </div>
          </div>
     );
}

export default EmployeeAttendance;